import { useState, useContext, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import Header from '../../header';
import Sidebar from '../../sidebar';
import ChatbotWidget from '../../chatbotWidget';
import Button from '../../button';
import EdenContext from '../../../context/edenContext';
import ErrorMessage from '../../errorMessage';
import NotAuthorized from '../../notAuthorized';
import Loader from '../../loader';
import { loginRequest } from '../../../authConfig';
import MicroSoftLogo from '../../../images/microsoft.svg';
import EdenLogo from '../../../images/logo.svg';
import RouteGuard from '../../../hooks/RouteGaurd';

import styles from './privateLayout.module.scss';

const PrivateRoutes = () => {
	const { instance, inProgress } = useMsal();
	const location = useLocation();

	const [loginError, setLoginError] = useState(false);

	const handleLogin = () => {
		setLoginError(false);
		instance.loginRedirect(loginRequest).catch((err) => {
			console.error('Login redirect failed:', err);
			setLoginError(true);
		});
	};

	const {
		isAuthenticated,
		authError,
		logout,
		userInfo,
		userInfoLoading,
		userInfoError,
		userType,
		allowedRoutes,
	} = useContext(EdenContext);

	const [collapsed, setCollapsed] = useState(false);

	const toggleCollapsed = () => {
		setCollapsed(!collapsed);
	};

	const customTitles = {
		'/': 'Home',
		'/rfq-queue': 'China RFQ Queue',
	};

	const generateTitle = (path) => {
		if (customTitles[path]) return customTitles[path];

		const parts = path.split('/').filter(Boolean);
		return parts
			.map((segment) =>
				segment
					.replace(/-/g, ' ')
					.replace(/\b\w/g, (char) => char.toUpperCase()),
			)
			.join(' - ');
	};

	useEffect(() => {
		const title = generateTitle(location.pathname);
		document.title = `EDEN - ${title}` || 'EDEN'; //eslint-disable-next-line
	}, [location.pathname]);

	useEffect(() => {
		const handleKeyPress = (event) => {
			if (event.ctrlKey && event.key === 'b') {
				toggleCollapsed();
			}
		};

		window.addEventListener('keydown', handleKeyPress);

		return () => {
			window.removeEventListener('keydown', handleKeyPress);
		}; //eslint-disable-next-line
	}, [collapsed]);

	const isSigningIn = !isAuthenticated && inProgress !== InteractionStatus.None;

	const STUCK_TIMEOUT_MS = 5000;
	const [stuckTimedOut, setStuckTimedOut] = useState(false);

	useEffect(() => {
		if (!isSigningIn) {
			setStuckTimedOut(false);
			return undefined;
		}

		const timer = setTimeout(() => setStuckTimedOut(true), STUCK_TIMEOUT_MS);
		return () => clearTimeout(timer);
	}, [isSigningIn]);

	const handleResetStuckSignIn = () => {
		localStorage.clear();
		window.location.assign('/');
	};

	let stage = 'idle';
	if (isSigningIn) {
		stage = stuckTimedOut ? 'stuck' : 'busy';
	} else if (authError === 'wrong-domain') {
		stage = 'error';
	}

	const authCopy = {
		idle: {
			heading: 'Sign in to continue',
			body: 'Use your Supreme Components Microsoft account.',
		},
		busy: {
			heading: 'Signing you in…',
			body: 'Confirming your Microsoft account.',
		},
		stuck: {
			heading: 'This is taking longer than expected',
			body: 'Your sign-in didn’t finish - this can happen if a previous attempt was interrupted.',
		},
		error: {
			heading: 'This isn’t a Supreme Components account',
			body: 'EDEN only grants access to Supreme Components Microsoft accounts.',
		},
	}[stage];

	return (
		<>
			{isAuthenticated ? (
				<>
					{userInfoError && !userInfoLoading && (
						<div className={styles['error-wrapper']}>
							<ErrorMessage message="Snap! That sound could be that of your data endpoint going kaput. But don’t worry our engineers are already on it getting it fixed." />
						</div>
					)}

					{userInfoLoading && (
						<div className={styles['loading-wrapper']}>
							<Loader />
						</div>
					)}

					{!userInfoLoading &&
						!userInfoError &&
						userInfo?.ViewAsLoggedinUser?.isActive === false && (
							<NotAuthorized />
						)}

					{!userInfoLoading &&
						!userInfoError &&
						userInfo?.ViewAsLoggedinUser?.isActive === true && (
							<>
								{!userInfoLoading && !userInfoError && (
									<div className={styles['layout-wrapper']}>
										<ChatbotWidget />
										<div className={styles['header']}>
											<Header />
										</div>
										<div className={styles['layout']}>
											<div
												className={`${styles['sidebar-content']} ${
													collapsed === true
														? styles['collapsed-sidebar']
														: userType === 'sm'
															? styles['sidebar-sm']
															: styles['sidebar']
												}`}
											>
												<Sidebar
													collapsed={collapsed}
													toggleCollapsed={toggleCollapsed}
													setCollapsed={setCollapsed}
												/>
											</div>
											<div
												className={`${styles['cont']} ${
													collapsed
														? styles['collapsed-content']
														: userType === 'sm'
															? styles['content-sm']
															: styles['content']
												}`}
											>
												<RouteGuard allowedRoutes={allowedRoutes}>
													<Outlet />
												</RouteGuard>
											</div>
										</div>
									</div>
								)}
							</>
						)}
				</>
			) : (
				<div className={styles['authWrapper']}>
					<div className={styles['authStage']}>
						<div className={styles['authCard']}>
							<img src={EdenLogo} alt="" className={styles['authBadge']} />
							{stage === 'stuck' && (
								<span
									className={`${styles['authEyebrow']} ${styles['authEyebrowWarning']}`}
								>
									Sign-in interrupted
								</span>
							)}
							{stage === 'error' && (
								<span
									className={`${styles['authEyebrow']} ${styles['authEyebrowError']}`}
								>
									Access restricted
								</span>
							)}
							{(stage === 'idle' || stage === 'busy') && (
								<span className={styles['authEyebrow']}>Welcome to EDEN</span>
							)}
							<h1 className={styles['authHeading']}>{authCopy.heading}</h1>
							<p className={styles['authBody']}>{authCopy.body}</p>

							{stage === 'idle' && (
								<div className={styles['authAction']} key="idle">
									<button
										type="button"
										className={styles['msButton']}
										onClick={handleLogin}
									>
										<img className={styles['img']} src={MicroSoftLogo} alt="" />
										<span className={styles['span']}>
											Sign in with Microsoft
										</span>
									</button>
									{loginError && (
										<div className={styles['authError']}>
											Sign-in failed. Please try again.
										</div>
									)}
								</div>
							)}

							{stage === 'busy' && (
								<div
									className={`${styles['authAction']} ${styles['authSpinnerRow']}`}
									key="busy"
								>
									<span className={styles['authSpinner']} />
									<span>Please wait…</span>
								</div>
							)}

							{stage === 'stuck' && (
								<div className={styles['authAction']} key="stuck">
									<Button
										color="orange"
										onClick={handleResetStuckSignIn}
										style={{ width: '100%' }}
									>
										Reset and try again
									</Button>
								</div>
							)}

							{stage === 'error' && (
								<div className={styles['authAction']} key="error">
									<Button
										color="red"
										onClick={logout}
										style={{ width: '100%' }}
									>
										Sign out and try again
									</Button>
								</div>
							)}
						</div>
					</div>

					<div className={styles['authFooter']}>
						Supreme Components International · EDEN
					</div>
				</div>
			)}
		</>
	);
};

export default PrivateRoutes;
